import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { DataUnit, SourceTotal } from '@/core/types';
import { DataCorrectionStatusEnum, DUItemStatusEnum } from '@/core/enums';
import { formatDate, formatNumber } from '@/core/helpers';
import { ControlledCollapse } from '../Collapse/ControlledCollapse';
import InvoiceDataSourceItem from '../ManualDataUploader/InvoiceDataSourceItem';

import Arrow from '@/icons/select-chevron.svg?react';
import ManualFileUploader from './ManualFileUploader';

import { SourceIcon } from '../SourceIcon';
import CheckIcon from '@/icons/check.svg?react';

const SOURCES_LABELS: {
  [key: string]: string;
} = {
  amazon: 'Amazon',
  easybill: 'Easybill',
  billbee: 'Billbee',
  manual: 'Manual',
  jtl: 'JTL',
};

interface Props {
  unit: DataUnit;
  sourceTotals: SourceTotal[] | undefined;
  onClickDeleteFile: (id: number) => void;
  deadlineDate: Date;
  isAllDataProvided: boolean;
  isProcessingFiles: boolean;
}

export default function ProvideDataToDo({
  unit,
  sourceTotals,
  onClickDeleteFile,
  deadlineDate,
  isAllDataProvided,
  isProcessingFiles,
}: Props) {
  const { t } = useTranslation();
  const currDate = new Date();

  const sortedUnitItems = unit.items.sort((a, b) => {
    if (a.source_type === 'manual' && b.source_type === 'manual') return -1;

    return a.source_type.localeCompare(b.source_type);
  });

  const isDataBlocked =
    !!unit.confirmation_date &&
    unit.correction_status === DataCorrectionStatusEnum.Blocked;

  return (
    <div data-testid='provide-data-todo-collapse'>
      <ControlledCollapse
        initialOpened={!isAllDataProvided && !isDataBlocked}
        className={classNames('mt-2 rounded-lg border p-3', {
          'border-[rgba(252,17,17,0.10)] bg-orange-200': !isAllDataProvided,
          'border-[rgba(13,168,107,0.06)] bg-[rgba(13,168,107,0.06)]':
            isAllDataProvided && !isDataBlocked,
        })}
        triggerRenderer={(isOpened, toggleCollapse) => (
          <div
            className={`flex cursor-pointer items-center justify-between gap-x-3 ${
              isOpened ? 'rounded-t-lg' : 'rounded-lg'
            }`}
            onClick={toggleCollapse}
          >
            <div className='flex flex-grow items-center justify-between font-bold'>
              <div className='flex flex-grow items-center gap-x-2'>
                {isDataBlocked ? (
                  <span>{t('Provided data')}</span>
                ) : (
                  <>
                    <span>1. {t('Provide data')}</span>
                    {isAllDataProvided && <CheckIcon />}
                  </>
                )}
              </div>
              <ul className='flex gap-x-3'>
                {sortedUnitItems
                  .filter((ui) => ui.files.length)
                  .map((item) => (
                    <li
                      key={item.id}
                      className='flex-end flex gap-x-1 font-bold'
                    >
                      <SourceIcon
                        source={item.source_type}
                        className='h-6 w-6'
                      />
                      {item.source_type === 'amazon' ? (
                        <>
                          <span>{item.files.length}</span>
                          <span>
                            {item.files.length > 1
                              ? t('Seller accounts')
                              : t('Seller account')}
                          </span>
                        </>
                      ) : item.source_type === 'manual' ? (
                        <>
                          <span>{item.files.length}</span>
                          <span>
                            {t(`${SOURCES_LABELS[item.source_type]}`)}{' '}
                            {item.files.length > 1 ? t('files') : t('file')}
                          </span>
                        </>
                      ) : (
                        <span>
                          {t(`${SOURCES_LABELS[item.source_type]}`)} {t('data')}
                        </span>
                      )}
                    </li>
                  ))}
              </ul>
            </div>
            <Arrow
              className={classNames(
                'ProvideDataToDo_Collapse SellerPastPeriodsFiling__arrow',
                {
                  'SellerPastPeriodsFiling__arrow--active': isOpened,
                }
              )}
            />
          </div>
        )}
        contentRenderer={() => (
          <>
            {sortedUnitItems.map((dataUnitSourceItem) => {
              const isManualSource =
                dataUnitSourceItem.source_type === 'manual';

              const dataSourceTransactions =
                sourceTotals?.find(
                  (item) => item.data_source === dataUnitSourceItem.source_type
                )?.transactions || null;

              const hasUploadedMUFile =
                dataUnitSourceItem.source_type === 'manual' &&
                !!dataUnitSourceItem.files.length;

              const isMUNeeded =
                dataUnitSourceItem.source_type === 'manual' &&
                dataUnitSourceItem.status === DUItemStatusEnum.NEW;

              const noDataNeeded =
                dataUnitSourceItem.source_type === 'manual' &&
                dataUnitSourceItem.no_data_needed;

              return (
                <InvoiceDataSourceItem
                  onClickDeleteFile={onClickDeleteFile}
                  isAllDataProvided={isAllDataProvided || false}
                  key={dataUnitSourceItem.source_type}
                  source={dataUnitSourceItem.source_type}
                  files={dataUnitSourceItem.files}
                  isDUDataBlocked={isDataBlocked}
                  deadlineDescription={
                    isManualSource ? (
                      <>
                        :{' '}
                        {!hasUploadedMUFile && noDataNeeded && (
                          <span>{t('Not needed for this period')}</span>
                        )}
                        {!hasUploadedMUFile && !noDataNeeded ? (
                          isMUNeeded ? (
                            <>
                              <span>{t('not uploaded yet')}</span>
                              <span
                                className={`pl-2 ${
                                  noDataNeeded ? 'text-dark' : 'text-orange-500'
                                }`}
                              >
                                {t('by *deadline*').replace(
                                  '*deadline*',
                                  formatDate(deadlineDate, 'E, do MMM')
                                )}
                              </span>
                            </>
                          ) : (
                            <span>{t('not uploaded')}</span>
                          )
                        ) : null}
                        {hasUploadedMUFile && !!dataSourceTransactions && (
                          <span>
                            {formatNumber(dataSourceTransactions)}{' '}
                            {t('transactions')}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        :{' '}
                        {currDate.getDate() <= 5 &&
                        !dataUnitSourceItem.files.length &&
                        !dataSourceTransactions ? (
                          <span>{t('will be imported on 5th')}</span>
                        ) : !!dataUnitSourceItem.files.length &&
                          dataSourceTransactions ? (
                          <span>
                            {formatNumber(dataSourceTransactions)}{' '}
                            {t('transactions')}
                          </span>
                        ) : null}
                      </>
                    )
                  }
                  uploadFileForm={
                    isManualSource && !isDataBlocked ? (
                      <ManualFileUploader
                        noDataNeeded={noDataNeeded}
                        isMUNeeded={isMUNeeded}
                        unitDate={unit.date}
                        hasUploadedMUFile={hasUploadedMUFile}
                        isProcessingFiles={isProcessingFiles}
                      />
                    ) : undefined
                  }
                />
              );
            })}
          </>
        )}
      />
    </div>
  );
}
